import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@client';
import { FileItem } from '../../types';

export interface PortCallFeePaymentResponse {
  id: number;
  buyerId?: number;
  evaluationId?: number;
  portCallId: number;
  portCallReference: string;
  approvedOn: string;
  vesselName: string;
  portName: string;
  activity: string;
  feeAmount: number;
  currency: string;
  invoicingPeriod: string;
}

export interface PortCallFeePaymentDetailResponse {
  payment: PortCallFeePaymentResponse;
  buyerName: string;
  settledOn?: string;
  cashFlowAccountName?: string;
  exchangeRate?: number;
  remittanceNotes?: string;
  attachments: FileItem[];
}

export interface PortCallFeeSettlePreviewResponse {
  buyerId: number;
  buyerName: string;
  invoicingPeriod: string;
  currency: string;
  totalFeeAmount: number;
  paymentCount: number;
  payments: PortCallFeePaymentResponse[];
}

export interface PortCallFeeSettlementFailure {
  paymentId: number;
  reason: string;
}

export interface PortCallFeeBulkSettlementResult {
  succeeded: number[];
  failed: PortCallFeeSettlementFailure[];
}

export type PortCallFeePaymentListState = 'pending' | 'settled';

export interface PortCallFeeInvoicingPeriodsArgs {
  state: PortCallFeePaymentListState;
  buyerId?: number;
}

export interface PortCallFeeSettledGroupResponse {
  invoiceFileName?: string;
  invoiceDownloadLink?: string;
  settledOn: string;
  invoicingPeriod: string;
  currency: string;
  totalFeeAmount: number;
  paymentCount: number;
  payments: PortCallFeePaymentResponse[];
}

export interface PortCallFeeSettledGroupsPage {
  items: PortCallFeeSettledGroupResponse[];
  totalItems: number;
  hasNextPage: boolean;
}

export interface PortCallFeeSettledGroupsArgs {
  buyerId?: number;
  invoicingPeriod?: string;
  page: number;
  size: number;
}

export const portCallFeePaymentsApi = createApi({
  baseQuery: axiosBaseQuery(),
  tagTypes: ['PortCallFeePayments'],
  reducerPath: 'portcall-fee-payments_api',
  endpoints: (build) => ({
    fetchPortCallFeeInvoicingPeriods: build.query<
      string[],
      PortCallFeeInvoicingPeriodsArgs
    >({
      query: ({ state, buyerId }) => ({
        url: '/secured/financial/port-call-fee-payments/invoicing-periods',
        method: 'GET',
        params: {
          state: state.toUpperCase(),
          ...(buyerId != null ? { buyerId } : {})
        }
      }),
      providesTags: ['PortCallFeePayments']
    }),
    fetchPortCallFeeSettledGroups: build.query<
      PortCallFeeSettledGroupsPage,
      PortCallFeeSettledGroupsArgs
    >({
      query: ({ buyerId, invoicingPeriod, page, size }) => ({
        url: '/secured/financial/port-call-fee-payments/settled',
        method: 'GET',
        params: {
          page,
          size,
          ...(buyerId != null ? { buyerId } : {}),
          ...(invoicingPeriod ? { invoicingPeriod } : {})
        }
      }),
      providesTags: ['PortCallFeePayments']
    }),
    fetchPortCallFeeSettlePreview: build.query<
      PortCallFeeSettlePreviewResponse,
      { paymentIds: number[] }
    >({
      query: ({ paymentIds }) => ({
        url: '/secured/financial/port-call-fee-payments/settle-preview',
        method: 'GET',
        params: { paymentIds: paymentIds.join(',') }
      }),
      providesTags: ['PortCallFeePayments']
    }),
    fetchPortCallFeePayment: build.query<
      PortCallFeePaymentDetailResponse,
      number
    >({
      query: (paymentId) => ({
        url: `/secured/financial/port-call-fee-payments/${paymentId}`,
        method: 'GET'
      }),
      providesTags: (_result, _error, paymentId) => [
        { type: 'PortCallFeePayments', id: paymentId }
      ]
    }),
    settlePortCallFeePayments: build.mutation<
      PortCallFeeBulkSettlementResult,
      FormData
    >({
      query: (data) => ({
        url: '/secured/financial/port-call-fee-payments/settle',
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data
      }),
      invalidatesTags: ['PortCallFeePayments']
    })
  })
});

export type StateWithPortCallFeePaymentsApi = {
  [portCallFeePaymentsApi.reducerPath]: ReturnType<
    typeof portCallFeePaymentsApi.reducer
  >;
};

export const {
  useFetchPortCallFeeInvoicingPeriodsQuery,
  useFetchPortCallFeeSettledGroupsQuery,
  useFetchPortCallFeeSettlePreviewQuery,
  useFetchPortCallFeePaymentQuery,
  useSettlePortCallFeePaymentsMutation
} = portCallFeePaymentsApi;
